/**
 * Cookie Toggle Component
 * A visually appealing custom toggle switch with animations
 */

import { cn } from '@/lib/utils';
import { DIALOG_CONTENT } from '../constants';

interface CookieToggleProps {
  /** Whether the toggle is checked */
  checked: boolean;
  /** Called when toggle state changes */
  onChange: (checked: boolean) => void;
  /** Whether the toggle is disabled (always on) */
  disabled?: boolean;
  /** Accessible label */
  label: string;
}

export function CookieToggle({ checked, onChange, disabled = false, label }: CookieToggleProps) {
  const statusText = disabled
    ? DIALOG_CONTENT.toggle.alwaysActive
    : checked
      ? DIALOG_CONTENT.toggle.active
      : DIALOG_CONTENT.toggle.inactive;

  return (
    <div className="flex items-center gap-3">
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        className={cn(
          'relative inline-flex h-7 w-12 shrink-0 cursor-pointer items-center rounded-full transition-all duration-300 ease-out',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#55a5db] focus-visible:ring-offset-2',
          disabled && 'cursor-not-allowed',
          checked || disabled
            ? disabled
              ? 'bg-emerald-500/90'
              : 'bg-[#55a5db]'
            : 'bg-gray-300'
        )}
      >
        {/* Track highlight effect */}
        <span
          className={cn(
            'absolute inset-0 rounded-full transition-opacity duration-300',
            (checked || disabled) && 'bg-gradient-to-r from-white/20 to-transparent opacity-100',
            !checked && !disabled && 'opacity-0'
          )}
        />

        {/* Thumb */}
        <span
          className={cn(
            'pointer-events-none block h-5 w-5 rounded-full bg-white shadow-lg ring-0 transition-all duration-300 ease-out',
            checked || disabled ? 'translate-x-6' : 'translate-x-1',
            // Subtle press effect
            'active:scale-95'
          )}
        >
          {/* Inner glow when active */}
          <span
            className={cn(
              'absolute inset-0 rounded-full transition-opacity duration-300',
              (checked || disabled) && 'shadow-[0_0_8px_rgba(85,165,219,0.5)]',
              disabled && 'shadow-[0_0_8px_rgba(16,185,129,0.5)]'
            )}
          />
        </span>
      </button>

      {/* Status text */}
      <span
        className={cn(
          'text-xs font-medium transition-colors duration-200',
          disabled
            ? 'text-emerald-600'
            : checked
              ? 'text-[#55a5db]'
              : 'text-gray-500'
        )}
      >
        {statusText}
      </span>
    </div>
  );
}
